import { makeAutoObservable } from "mobx";
import { loadAttrTypeMap } from "src/common/attributes";
import { onError } from "src/common/onError";
import {
  CtrlColumns,
  TableLoadParams,
  TableStore,
} from "src/components/tables/TableStore";
import { compoundEntityTableStore2 } from "src/pages/EntityFiltersPage/EntityList/compoundEntityTableStore2";
import {
  EntityFilters,
  ZEntityRow,
} from "src/pages/EntityFiltersPage/EntityList/types";
import { ZAttribute } from "src/types/ZAttribute";
import { ZObjState } from "src/types/ZObjState";
import { ZUid } from "src/types/zUid";
import { loadObjectStates, makeObjectStatesMap } from "../../roles/rolesApi";
import {
  ZDisplayAttr,
  ZObjTableSettings,
  TableSettingsType,
} from "../ObjTableSettingsTypes";
import { getColumnSettings } from "./getColumnSettings";
import { reconcileObjTableSettings } from "../reconcileObjTableSettings";

export class ObjTableControlStore {
  constructor(
    private loading: () => boolean,
    private setLoading: (flag: boolean) => void,
    private save: () => Promise<void>,
    private disabled: () => boolean,
    readonly sortableAttrs: ZAttribute[],
  ) {
    makeAutoObservable(this);
  }

  tableStore: TableStore<ZEntityRow, EntityFilters> | null = null;

  setTableStore(store: TableStore<ZEntityRow, EntityFilters>) {
    this.tableStore = store;
  }

  type: TableSettingsType | null = null;

  setType(newType: TableSettingsType) {
    this.type = newType;
  }

  displayAttrs: ZDisplayAttr[] | null = null;

  setDisplayAttrs(attrs: ZDisplayAttr[] | null) {
    this.displayAttrs = attrs;
  }

  objectStates: ZObjState[] = [];

  setObjectStates(list: ZObjState[]) {
    this.objectStates = list;
  }

  typesMap: Record<number, string> = {};

  setTypesMap(map: Record<number, string>) {
    this.typesMap = map;
  }

  async init(objectId: ZUid, initialSettings: ZObjTableSettings) {
    const { sort, type } = initialSettings;
    const sortableAttributeIds = new Set(
      this.sortableAttrs.map(({ id }) => String(id)),
    );
    const actualSort =
      sort && sortableAttributeIds.has(String(sort.attributeId)) ? sort : null;
    this.setType(type);
    const initialParams: TableLoadParams<EntityFilters> = {
      page: 0,
      pageSize: 3,
      sort: actualSort ? String(actualSort.attributeId) : undefined,
      sortOrder: actualSort ? actualSort.direction : undefined,
      filters: { objectId },
    };
    try {
      this.setLoading(true);
      this.setObjectStates(await loadObjectStates(objectId));
      this.setTypesMap(await loadAttrTypeMap());
      const tableStore = await compoundEntityTableStore2(
        objectId,
        "id",
        this.typesMap,
        makeObjectStatesMap(this.objectStates),
        initialParams,
        undefined,
        () => {
          if (this.displayAttrs?.length) {
            this.setTableSettings(this.displayAttrs, initialParams);
            this.setDisplayAttrs(null);
          }
        },
        { unsortable: true, unsavable: true },
      );
      const reconciledSettings = reconcileObjTableSettings(
        initialSettings,
        new Set(tableStore.columns.map(({ key }) => key)),
        sortableAttributeIds,
      );
      this.setDisplayAttrs(reconciledSettings.displayAttributes);
      this.setTableStore(tableStore);
    } catch (error) {
      onError(error);
    } finally {
      this.setLoading(false);
    }
  }

  setTableSettings(
    columns: ZDisplayAttr[],
    params?: TableLoadParams<EntityFilters>,
  ) {
    const { tableStore } = this;
    // если массив с колонками пуст - это значит, что настройка для них не производилась
    if (!tableStore || !columns) return;
    const settingsColumns = new Map(
      columns.map(({ id, settings }) => [String(id), settings]),
    );
    // порядок;
    tableStore.setColumnOrder(columns.map(({ id }) => String(id)));
    tableStore.columns.forEach(({ key }) => {
      // ширина;
      const colSettings = settingsColumns.get(key);
      tableStore.setColumnWidth(
        key,
        colSettings ? (getColumnSettings(colSettings)?.width ?? 150) : 150,
      );
      // видимость;
      tableStore.setColumnVisible(key, !!colSettings);
    });
    // параметры
    if (params) {
      tableStore.setParams(params);
      tableStore.reload();
    }
  }

  get columnStore(): CtrlColumns | null {
    const { tableStore } = this;
    if (!tableStore) return null;
    return {
      ...(tableStore as CtrlColumns),
      isColumnVisible: (key) => tableStore.isColumnVisible(key),
      canColumnHide: (key) => tableStore.canColumnHide(key),
      setColumnVisible: async (key, visible) => {
        tableStore.setColumnVisible(key, visible);
        await this.save();
      },
      setColumnOrder: async (newOrder) => {
        tableStore.setColumnOrder(newOrder);
        await this.save();
      },
    };
  }

  saveSettings() {
    this.save();
  }

  get isLoading(): boolean {
    return this.loading();
  }

  get isDisabled(): boolean {
    return this.isLoading || this.disabled();
  }

  get settings(): ZObjTableSettings | null {
    const { type, tableStore } = this;
    if (!tableStore || !type) return null;
    const { sort: tableSort, sortOrder, finalColumns } = tableStore;
    const sort =
      tableSort && sortOrder
        ? {
            attributeId: tableSort,
            direction: sortOrder,
          }
        : null;
    const displayAttributes = finalColumns.map(({ key, width }) => {
      const id = key;
      return {
        id,
        settings: JSON.stringify({ width: width ? Number(width) : undefined }),
      };
    });
    return {
      type,
      sort,
      displayAttributes,
    };
  }
}
